import { useState, useMemo } from "react";
import { useUnsavedChanges, useSubmitGuard } from "@/hooks/useFormSafety";
import { useAppData } from "@/data/AppDataContext";
import { PermissionGate } from "@/components/PermissionGate";
import { useJobActions } from "@/hooks/useJobActions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle, AlertTriangle, ChevronRight, Sparkles } from "lucide-react";
import type { IssueSeverity, IssueBlockerCategory, IssueAffectedArea } from "@/data/types";
import { formatAddress } from "@/lib/addressUtils";
import { classifyIssue } from "@/data/situational/issueTypeClassifier";

const severities: IssueSeverity[] = ["Low", "Medium", "Urgent"];

const blockerCategories: IssueBlockerCategory[] = [
  "Missing Material", "Permit Rejection", "HOA Documentation", "Crew Unavailable",
  "Customer Unavailable", "Damaged Material", "Design Error", "Scheduling Conflict",
  "Weather Delay", "Supplier Delay", "Payment Hold", "Inspection Failure",
  "Pending Identification", "Other",
];

const affectedAreas: IssueAffectedArea[] = [
  "Shower Base", "Glass Panel", "Glass Door", "Plumbing", "Tile", "Fixtures",
  "Vanity", "Framing", "Waterproofing", "Electrical", "General", "Scheduling",
  "Materials", "Permit", "Other",
];

const STEPS = [
  { id: "job", label: "Job", fields: ["jobId"] },
  { id: "details", label: "Issue Details", fields: ["severity", "affectedArea", "description"] },
  { id: "blocker", label: "Blocker & Concern", fields: ["blockerCategory", "blockerDetail"] },
  { id: "action", label: "Action & Assignment", fields: ["suggestedAction", "owner"] },
] as const;

export default function IssueForm() {
  const { jobs, contractors } = useAppData();
  const { createIssue: orchestratedCreateIssue } = useJobActions();
  const [step, setStep] = useState(0);

  // Form fields
  const [jobId, setJobId] = useState("");
  const [severity, setSeverity] = useState<IssueSeverity | "">("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [blockerCategory, setBlockerCategory] = useState<IssueBlockerCategory | "">("");
  const [blockerDetail, setBlockerDetail] = useState("");
  const [homeownerConcern, setHomeownerConcern] = useState("");
  const [affectedArea, setAffectedArea] = useState<IssueAffectedArea | "">("");
  const [suggestedAction, setSuggestedAction] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  const selectedJob = jobs.find((j) => j.id === jobId);

  // Derived classification — replaces the old generic "Issue Type" dropdown.
  const classification = useMemo(
    () => classifyIssue({ blockerCategory, affectedArea, description }),
    [blockerCategory, affectedArea, description],
  );

  const isDirty = useMemo(
    () => !!(jobId || description || severity || blockerCategory || affectedArea),
    [jobId, description, severity, blockerCategory, affectedArea],
  );
  useUnsavedChanges(isDirty);
  const { isSubmitting, guard } = useSubmitGuard();

  // Validation per step
  const stepValid = (s: number): boolean => {
    switch (s) {
      case 0: return !!jobId;
      case 1: return !!severity && !!affectedArea && description.trim().length >= 10;
      case 2: return !!blockerCategory && blockerDetail.trim().length >= 5;
      case 3: return suggestedAction.trim().length >= 5 && classification.confident;
      default: return false;
    }
  };

  const allValid = STEPS.every((_, i) => stepValid(i));
  const completedSteps = STEPS.filter((_, i) => stepValid(i)).length;
  const progress = Math.round((completedSteps / STEPS.length) * 100);

  const handleSubmit = () => guard(() => {
    if (!allValid) {
      toast.error("Please complete all required fields.");
      return;
    }
    const result = orchestratedCreateIssue({
      jobId,
      issueType: classification.issueType,
      severity: severity as IssueSeverity,
      description,
      owner: owner || "Unassigned",
      source: "Admin",
      relatedContractorId: contractorId || undefined,
      blockerCategory: blockerCategory as IssueBlockerCategory,
      blockerDetail,
      homeownerConcern: homeownerConcern || undefined,
      affectedArea: affectedArea as IssueAffectedArea,
      suggestedAction,
      jobStageAtCreation: selectedJob?.currentStage || undefined,
    });
    if (!result.success) {
      toast.error(result.error || "Failed to create issue");
      return;
    }
    toast.success(`Issue reported: ${classification.label}`);
    // Reset
    setStep(0);
    setJobId(""); setSeverity(""); setDescription("");
    setOwner(""); setContractorId(""); setBlockerCategory(""); setBlockerDetail("");
    setHomeownerConcern(""); setAffectedArea(""); setSuggestedAction(""); setAdditionalNotes("");
  });

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Report Service Issue</h1>
        <Badge variant="outline" className="text-xs">
          {completedSteps}/{STEPS.length} sections complete
        </Badge>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <Progress value={progress} className="h-2" />
        <div className="flex gap-1">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`flex-1 text-[10px] py-1.5 px-2 rounded border transition-all text-center ${
                step === i
                  ? "bg-primary text-primary-foreground border-primary"
                  : stepValid(i)
                  ? "bg-success/10 text-success border-success/30"
                  : "bg-muted/50 text-muted-foreground border-border"
              }`}
            >
              <span className="flex items-center justify-center gap-1">
                {stepValid(i) && <CheckCircle className="h-3 w-3" />}
                {s.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">

          {/* Step 0: Job */}
          {step === 0 && (
            <div className="space-y-4">
              <CardHeader className="p-0">
                <CardTitle className="text-sm">Step 1: Select Job</CardTitle>
              </CardHeader>
              <div>
                <Label>Linked Job <span className="text-destructive">*</span></Label>
                <Select value={jobId} onValueChange={setJobId}>
                  <SelectTrigger><SelectValue placeholder="Select job" /></SelectTrigger>
                  <SelectContent>
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.customerName} — {j.id} — {j.currentStage}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedJob && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {formatAddress(selectedJob.address)} • Stage: {selectedJob.currentStage}
                  </p>
                )}
              </div>
              <div>
                <Label>Related Contractor</Label>
                <Select value={contractorId} onValueChange={setContractorId}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contractors.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.companyName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground border-l-2 border-primary/40 pl-2 italic">
                Issue type is auto-classified from the details you enter in the next steps — no generic picker.
              </p>
            </div>
          )}

          {/* Step 1: Details */}
          {step === 1 && (
            <div className="space-y-4">
              <CardHeader className="p-0">
                <CardTitle className="text-sm">Step 2: Issue Details</CardTitle>
              </CardHeader>
              <div>
                <Label>Severity <span className="text-destructive">*</span></Label>
                <Select value={severity} onValueChange={(v) => setSeverity(v as IssueSeverity)}>
                  <SelectTrigger><SelectValue placeholder="Select severity" /></SelectTrigger>
                  <SelectContent>
                    {severities.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Affected Area <span className="text-destructive">*</span></Label>
                <Select value={affectedArea} onValueChange={(v) => setAffectedArea(v as IssueAffectedArea)}>
                  <SelectTrigger><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>
                    {affectedAreas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description <span className="text-destructive">*</span> <span className="text-muted-foreground text-[10px]">(min 10 chars)</span></Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what is wrong in detail..."
                  rows={4}
                />
                {description.length > 0 && description.length < 10 && (
                  <p className="text-[10px] text-destructive mt-1">Description must be at least 10 characters</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Blocker & Concern */}
          {step === 2 && (
            <div className="space-y-4">
              <CardHeader className="p-0">
                <CardTitle className="text-sm">Step 3: Blocker & Homeowner Concern</CardTitle>
              </CardHeader>
              <div>
                <Label>Blocker Category <span className="text-destructive">*</span></Label>
                <Select value={blockerCategory} onValueChange={(v) => setBlockerCategory(v as IssueBlockerCategory)}>
                  <SelectTrigger><SelectValue placeholder="What is blocking progress?" /></SelectTrigger>
                  <SelectContent>
                    {blockerCategories.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Blocker Detail <span className="text-destructive">*</span> <span className="text-muted-foreground text-[10px]">(min 5 chars)</span></Label>
                <Textarea
                  value={blockerDetail}
                  onChange={(e) => setBlockerDetail(e.target.value)}
                  placeholder="Describe exactly what is blocking progress..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Homeowner Concern <span className="text-muted-foreground text-[10px]">(if applicable)</span></Label>
                <Textarea
                  value={homeownerConcern}
                  onChange={(e) => setHomeownerConcern(e.target.value)}
                  placeholder="What has the customer reported or expressed concern about?"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Step 3: Action & Assignment */}
          {step === 3 && (
            <div className="space-y-4">
              <CardHeader className="p-0">
                <CardTitle className="text-sm">Step 4: Action Required & Assignment</CardTitle>
              </CardHeader>

              {/* Auto-classification readout */}
              <div className={`rounded-lg border p-3 ${
                classification.confident
                  ? "bg-primary/5 border-primary/30"
                  : "bg-warning/5 border-warning/30"
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className={`h-3.5 w-3.5 ${classification.confident ? "text-primary" : "text-warning"}`} />
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                    Auto-Classified Type
                  </span>
                </div>
                <p className="text-sm font-semibold">{classification.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{classification.rationale}</p>
                {!classification.confident && (
                  <p className="text-[11px] text-warning mt-1">
                    Add more detail in Steps 2-3 so the system can classify this issue.
                  </p>
                )}
              </div>

              <div>
                <Label>Suggested Action <span className="text-destructive">*</span></Label>
                <Textarea
                  value={suggestedAction}
                  onChange={(e) => setSuggestedAction(e.target.value)}
                  placeholder="What specific action should be taken to resolve this?"
                  rows={3}
                />
              </div>
              <div>
                <Label>Owner / Assignee</Label>
                <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who should handle this?" />
              </div>
              <div>
                <Label>Additional Notes <span className="text-muted-foreground text-[10px]">(optional)</span></Label>
                <Textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any other context..."
                  rows={2}
                />
              </div>

              {/* Preview */}
              {allValid && (
                <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                  <p className="text-xs font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-warning" /> Issue Preview
                  </p>
                  <div className="space-y-1 text-[11px]">
                    <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{classification.label}</span></div>
                    <div><span className="text-muted-foreground">Issue:</span> <span className="font-medium">{description.slice(0, 80)}{description.length > 80 ? "..." : ""}</span></div>
                    <div><span className="text-muted-foreground">Stage:</span> <span className="font-medium">{selectedJob?.currentStage || "—"}</span></div>
                    <div><span className="text-muted-foreground">Blocker:</span> <span className="font-medium">{blockerDetail.slice(0, 80)}</span></div>
                    {homeownerConcern && <div><span className="text-muted-foreground">Homeowner Concern:</span> <span className="font-medium">{homeownerConcern.slice(0, 80)}</span></div>}
                    <div><span className="text-muted-foreground">Action:</span> <span className="font-medium">{suggestedAction.slice(0, 80)}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              disabled={step === 0}
              onClick={() => setStep((s) => s - 1)}
            >
              Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                size="sm"
                disabled={!stepValid(step)}
                onClick={() => setStep((s) => s + 1)}
                className="gap-1"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <PermissionGate permission="issues_create">
                <Button
                  size="sm"
                  disabled={!allValid || isSubmitting.current}
                  onClick={handleSubmit}
                  className="gap-1"
                >
                  <CheckCircle className="h-3.5 w-3.5" /> {isSubmitting.current ? "Submitting…" : "Submit Issue"}
                </Button>
              </PermissionGate>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
